import {
  RESERVED_SALARY_SOURCE,
  SALARY_SOURCES,
  roundSalary,
  type SalaryDefinition,
} from './salaryDefinition';
import {
  SALARY_SOURCE_LABELS,
  TARGET_POOL_MAXIMUM,
  TARGET_POOL_MINIMUM,
} from './salaryWording';

/**
 * The client-side reading of what the server will accept for a salary model.
 *
 * <b>Feedback, never the control.</b> `SalaryModelDefinitionValidator` is what actually decides,
 * and it runs on every write and again at publish. What this buys is the thing that makes the
 * builder usable: an overlapping band is marked the moment it is made, rather than after a save
 * round trip.
 *
 * The rules are the server's, in the server's order, with the server's severities — and the
 * severities are the part worth not "tidying":
 *
 * - **A band gap is an error here where a points multiplier gap is a warning.** A metric matching
 *   no multiplier band yields 1.0, a defined answer. A composite percentile matching no salary
 *   band has no price at all, so the player is either undraftable or silently free.
 * - **A rounding mode that drives any price to zero is an error.** A free player is taken by every
 *   roster, so the budget stops binding and the contest has no constraint left. That is breaking
 *   the game rather than mispricing it, and it is reachable: a floor of 3 under
 *   `{ mode: "down", increment: 5 }` rounds to 0.
 * - **An inverted band is a warning.** Better players costing less is almost always a mistake and
 *   is still a product decision this layer does not get to overrule.
 * - **Two inputs reading one source is a warning.** 0.3 and 0.3 of the same figure is exactly 0.6
 *   of it, which computes — but it reads on the screen as two signals, and §4.3's whole argument
 *   is about which signals are real.
 *
 * The wording differs from the server's on purpose: the server writes for a log, this writes for
 * somebody running a card room.
 */

export type SalaryIssueSeverity = 'error' | 'warning';

export type SalaryIssueSection =
  | 'identity'
  | 'lookback'
  | 'inputs'
  | 'bands'
  | 'qualification'
  | 'unpriced';

export interface SalaryIssue {
  section: SalaryIssueSection;
  severity: SalaryIssueSeverity;
  message: string;
  /** Set when the issue is about one input or one band, so the editor can mark that line. */
  rowIndex?: number;
}

export interface SalaryValidation {
  issues: SalaryIssue[];
  errors: SalaryIssue[];
  warnings: SalaryIssue[];
  /** Whether the definition may be saved. Warnings never block. */
  isValid: boolean;
}

export function validateSalaryDefinition(definition: SalaryDefinition): SalaryValidation {
  const issues: SalaryIssue[] = [];

  checkIdentity(definition, issues);
  checkLookback(definition, issues);
  checkInputs(definition, issues);
  checkBands(definition, issues);
  checkQualification(definition, issues);
  // Runs last because it reads the bands: a band list that does not cover 0..100 has bigger
  // problems than what its salaries round to, and reporting both at once buries the first.
  if (!issues.some((i) => i.severity === 'error' && i.section === 'bands')) {
    checkRounding(definition, issues);
  }

  const errors = issues.filter((i) => i.severity === 'error');
  return {
    issues,
    errors,
    warnings: issues.filter((i) => i.severity === 'warning'),
    isValid: errors.length === 0,
  };
}

/** The issues one section of the builder should show. */
export function salaryIssuesFor(
  validation: SalaryValidation,
  section: SalaryIssueSection,
): SalaryIssue[] {
  return validation.issues.filter((i) => i.section === section);
}

export function salaryHasError(
  validation: SalaryValidation,
  section: SalaryIssueSection,
  rowIndex?: number,
): boolean {
  return validation.errors.some(
    (i) => i.section === section && (rowIndex === undefined || i.rowIndex === rowIndex),
  );
}

// ---------------------------------------------------------------- identity

function checkIdentity(definition: SalaryDefinition, issues: SalaryIssue[]) {
  if (definition.name.trim().length === 0) {
    issues.push({ section: 'identity', severity: 'error', message: 'Give this model a name.' });
  } else if (definition.name.trim().length > 120) {
    issues.push({
      section: 'identity',
      severity: 'error',
      message: 'The name is longer than 120 characters, which the server will refuse.',
    });
  }

  if ((definition.description ?? '').length > 500) {
    issues.push({
      section: 'identity',
      severity: 'error',
      message: 'The description is longer than 500 characters, which the server will refuse.',
    });
  }
}

// ---------------------------------------------------------------- lookback

function checkLookback(definition: SalaryDefinition, issues: SalaryIssue[]) {
  const { leaderboardIds, seriesCount } = definition.lookback;

  if (leaderboardIds.length > 0) return;

  if (seriesCount === null) {
    issues.push({
      section: 'lookback',
      severity: 'error',
      message:
        'Say how many past series to price from, or pick the leaderboards outright. With neither, there is no history to work from.',
    });
    return;
  }

  if (seriesCount < 1 || !Number.isInteger(seriesCount)) {
    issues.push({
      section: 'lookback',
      severity: 'error',
      message: 'The number of past series has to be a whole number, one or more.',
    });
  }
}

// ---------------------------------------------------------------- inputs

const ID_PATTERN = /^[A-Za-z][A-Za-z0-9_-]*$/;

function checkInputs(definition: SalaryDefinition, issues: SalaryIssue[]) {
  const inputs = definition.inputs;

  if (inputs.length === 0) {
    issues.push({
      section: 'inputs',
      severity: 'error',
      message: 'A model needs at least one thing to price on.',
    });
    return;
  }

  if (inputs.length > 6) {
    issues.push({
      section: 'inputs',
      severity: 'error',
      message: 'A model can read at most six things. Remove one.',
    });
  }

  const ids = new Set<string>();
  const sources = new Set<string>();

  inputs.forEach((input, index) => {
    const id = input.id.trim();

    if (id.length === 0) {
      issues.push({
        section: 'inputs',
        severity: 'error',
        rowIndex: index,
        message: 'Every input needs a short name, so a price can be read back to what built it.',
      });
    } else if (id.length > 40 || !ID_PATTERN.test(id)) {
      issues.push({
        section: 'inputs',
        severity: 'error',
        rowIndex: index,
        message:
          'A short name starts with a letter and holds only letters, numbers, dashes and underscores.',
      });
    } else if (ids.has(id.toLowerCase())) {
      issues.push({
        section: 'inputs',
        severity: 'error',
        rowIndex: index,
        message: `Two inputs are both called “${id}”. A price could not then be read back to the figures behind it.`,
      });
    } else {
      ids.add(id.toLowerCase());
    }

    // §4.4: listed in the grammar, refused by the rule, so the refusal can say *why*.
    if ((input.source as string) === RESERVED_SALARY_SOURCE) {
      issues.push({
        section: 'inputs',
        severity: 'error',
        rowIndex: index,
        message:
          'Player ratings are reserved for a later version and cannot be used yet. Ratings have to be built on who entered rather than on who cashed before they mean anything here.',
      });
    } else if (!(SALARY_SOURCES as readonly string[]).includes(input.source)) {
      issues.push({
        section: 'inputs',
        severity: 'error',
        rowIndex: index,
        message: `“${input.source}” is not something this model can read.`,
      });
    } else {
      if (sources.has(input.source)) {
        issues.push({
          section: 'inputs',
          severity: 'warning',
          rowIndex: index,
          // Named by what the operator sees, not by the document's own key: the short name is an
          // audit-trail id the builder generates and never shows, so a warning about "cashFreq"
          // would be about something not on the screen.
          message: `Two inputs both read “${SALARY_SOURCE_LABELS[input.source] ?? input.source}”, so their weights simply add. The model has fewer real signals than it looks like.`,
        });
      } else {
        sources.add(input.source);
      }
    }

    if (input.weight === null) {
      issues.push({
        section: 'inputs',
        severity: 'error',
        rowIndex: index,
        message: 'Give this input a weight.',
      });
    } else if (input.weight <= 0) {
      issues.push({
        section: 'inputs',
        severity: 'error',
        rowIndex: index,
        message:
          'A weight has to be above zero. To stop reading something, remove it rather than weighting it nothing.',
      });
    }
  });
}

// ---------------------------------------------------------------- bands

function checkBands(definition: SalaryDefinition, issues: SalaryIssue[]) {
  const bands = definition.mapping.bands;

  if (bands.length === 0) {
    issues.push({
      section: 'bands',
      severity: 'error',
      message: 'A model needs at least one price band.',
    });
    return;
  }

  if (bands.length > 20) {
    issues.push({
      section: 'bands',
      severity: 'error',
      message: 'Twenty price bands is the most a model can carry.',
    });
  }

  let previousTo: number | null = null;
  let previousSalary: number | null = null;

  bands.forEach((band, index) => {
    const { fromPercentile: from, toPercentile: to, salary } = band;

    if (from === null || to === null) {
      issues.push({
        section: 'bands',
        severity: 'error',
        rowIndex: index,
        message: `Band ${index + 1} is missing one of its edges.`,
      });
    }

    if (salary === null) {
      issues.push({
        section: 'bands',
        severity: 'error',
        rowIndex: index,
        message: `Band ${index + 1} has no price.`,
      });
    } else if (salary <= 0) {
      issues.push({
        section: 'bands',
        severity: 'error',
        rowIndex: index,
        message: `Band ${index + 1} prices players at nothing. A free player is on every roster, so the budget stops meaning anything.`,
      });
    }

    if (from === null || to === null) return;

    if (from < 0 || to > 100) {
      issues.push({
        section: 'bands',
        severity: 'error',
        rowIndex: index,
        message: `Band ${index + 1} runs outside the pool. Bands are shares of the pool, from 0 to 100.`,
      });
    }

    if (to <= from) {
      issues.push({
        section: 'bands',
        severity: 'error',
        rowIndex: index,
        message: `Band ${index + 1} is empty or backwards — it has to cover a stretch of the pool.`,
      });
    }

    if (previousTo === null) {
      if (from !== 0) {
        issues.push({
          section: 'bands',
          severity: 'error',
          rowIndex: index,
          message: `The first band starts at ${trim(from)} rather than 0, so the bottom ${trim(from)}% of the pool has no price at all.`,
        });
      }
    } else if (from !== previousTo) {
      issues.push({
        section: 'bands',
        severity: 'error',
        rowIndex: index,
        message:
          from > previousTo
            ? `Nothing covers ${trim(previousTo)} to ${trim(from)}. A player landing in that gap has no price.`
            : `Bands overlap between ${trim(from)} and ${trim(previousTo)}, so one player would have two prices.`,
      });
    }

    if (previousSalary !== null && salary !== null && salary < previousSalary) {
      issues.push({
        section: 'bands',
        severity: 'warning',
        rowIndex: index,
        message: `Band ${index + 1} costs less than the band below it. Better players are cheaper under this model.`,
      });
    }

    previousTo = to;
    if (salary !== null) previousSalary = salary;
  });

  if (previousTo !== null && previousTo !== 100) {
    issues.push({
      section: 'bands',
      severity: 'error',
      rowIndex: bands.length - 1,
      message: `The last band stops at ${trim(previousTo)} rather than 100, so the players a roster is built around have no price.`,
    });
  }
}

// ---------------------------------------------------------------- rounding and the floor

function checkRounding(definition: SalaryDefinition, issues: SalaryIssue[]) {
  const { increment } = definition.rounding;

  if (increment === null || increment <= 0) {
    issues.push({
      section: 'unpriced',
      severity: 'error',
      message: 'Prices have to round to something above zero.',
    });
    return;
  }

  const check = (salary: number | null, what: string, section: SalaryIssueSection) => {
    if (salary === null) return;
    const rounded = roundSalary(salary, definition.rounding);

    if (rounded <= 0) {
      issues.push({
        section,
        severity: 'error',
        message: `${what} is ${trim(salary)}, which rounds to ${trim(rounded)}. A free player is on every roster, so the budget stops meaning anything.`,
      });
    } else if (rounded !== salary) {
      issues.push({
        section,
        severity: 'warning',
        message: `${what} is ${trim(salary)} and will be charged at ${trim(rounded)} once rounded.`,
      });
    }
  };

  definition.mapping.bands.forEach((band, index) =>
    check(band.salary, `Band ${index + 1}’s price`, 'bands'),
  );

  if (definition.unpriced.salary === null) {
    issues.push({
      section: 'unpriced',
      severity: 'error',
      message: 'Give a price to players the model has no history for.',
    });
  } else if (definition.unpriced.salary <= 0) {
    issues.push({
      section: 'unpriced',
      severity: 'error',
      message:
        'A player with no history still has to cost something. A free player is on every roster.',
    });
  } else {
    check(definition.unpriced.salary, 'The price for players with no history', 'unpriced');
  }
}

// ---------------------------------------------------------------- qualification

function checkQualification(definition: SalaryDefinition, issues: SalaryIssue[]) {
  const { minCashes, minEvents, maxPoolSize } = definition.qualification;

  for (const [value, label] of [
    [minCashes, 'cashes'],
    [minEvents, 'events'],
  ] as const) {
    if (value !== null && (value < 0 || !Number.isInteger(value))) {
      issues.push({
        section: 'qualification',
        severity: 'error',
        message: `The minimum number of ${label} has to be a whole number, or left blank for no minimum.`,
      });
    }
  }

  if (maxPoolSize !== null && (maxPoolSize < 1 || !Number.isInteger(maxPoolSize))) {
    issues.push({
      section: 'qualification',
      severity: 'error',
      message: 'The pool cap has to be a whole number of players, or left blank for no cap.',
    });
    return;
  }

  // §4.5, mirroring the server's own warnings — the same rule stated for a different reader.
  const noMinimum = (minCashes ?? 0) === 0 && (minEvents ?? 0) === 0;

  if (maxPoolSize === null && noMinimum) {
    issues.push({
      section: 'qualification',
      severity: 'warning',
      message:
        'Nothing limits the pool, so everybody in the lookback is draftable. On the reference series that was 941 players, and 45% of entrants finished with no score at all.',
    });
  } else if (maxPoolSize !== null && maxPoolSize < TARGET_POOL_MINIMUM) {
    issues.push({
      section: 'qualification',
      severity: 'warning',
      message: `A cap of ${maxPoolSize} is below the ${TARGET_POOL_MINIMUM}–${TARGET_POOL_MAXIMUM} that works best. With too few players to choose from, every team ends up nearly the same.`,
    });
  } else if (maxPoolSize !== null && maxPoolSize > TARGET_POOL_MAXIMUM) {
    issues.push({
      section: 'qualification',
      severity: 'warning',
      message: `A cap of ${maxPoolSize} is above the ${TARGET_POOL_MINIMUM}–${TARGET_POOL_MAXIMUM} that works best. The bigger the pool, the more entrants pick nobody who turns up and finish on nothing.`,
    });
  }
}

function trim(value: number): string {
  return Number(value.toFixed(4)).toString();
}
